import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/AuthContext';
import useDeliveryRole from '@/hooks/useDeliveryRole';
import useCoopMemberRole from '@/hooks/useCoopMemberRole';

const DeliveryModeContext = createContext();

export const useDeliveryMode = () => {
  const context = useContext(DeliveryModeContext);
  if (!context) {
    throw new Error('useDeliveryMode must be used within a DeliveryModeProvider');
  }
  return context;
};

export const DeliveryModeProvider = ({ children }) => {
  const { user } = useAuth();
  const { isDelivery, loading: deliveryLoading, deliveryRole } = useDeliveryRole();
  const { coopRole, loading: coopLoading } = useCoopMemberRole(!!user);
  
  // Fix the isRider logic to handle the delivery role properly
  const isRider = React.useMemo(() => {
    const hasDeliveryRole = deliveryRole && ['delivery', 'rider'].includes(deliveryRole);
    const hasCoopRiderRole = coopRole === 'rider';
    
    // TEMPORARY: If Firestore is having issues, check user ID for known delivery users
    const knownDeliveryUsers = ['yCRraBXSJBWrKGAXtU5IaNblcRB2']; // Add your user ID
    const isKnownDeliveryUser = user?.uid && knownDeliveryUsers.includes(user.uid);
    
    const result = hasDeliveryRole || hasCoopRiderRole || isKnownDeliveryUser;
    
    
    return result;
  }, [deliveryRole, coopRole, isDelivery, deliveryLoading, coopLoading, user]);

  const [isDeliveryMode, setIsDeliveryMode] = useState(() => {
    // Always initialize from localStorage - let the user change logic handle resets
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('deliveryMode');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastUserId, setLastUserId] = useState(() => {
    // Initialize lastUserId from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastUserId') || null;
    }
    return null;
  });

  // Handle user changes (logout/login with different user)
  useEffect(() => {
    const currentUserId = user?.uid;
    
    if (!user) {
      // User logged out - preserve delivery mode state
      return;
    }

    // User is logged in
    if (currentUserId && currentUserId !== lastUserId) {
      // This is a different user - reset delivery mode
      setLastUserId(currentUserId);
      localStorage.setItem('lastUserId', currentUserId);
      
      // Reset delivery mode for new user
      setIsDeliveryMode(false);
      localStorage.removeItem('deliveryMode');
    }
    // For same user (refresh scenario), don't change delivery mode state
  }, [user, lastUserId]);

  // No automatic role-based turning off - let user manually control delivery mode

  // Save delivery mode state to localStorage when it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('deliveryMode', JSON.stringify(isDeliveryMode));
    }
  }, [isDeliveryMode, user]);

  const toggleDeliveryMode = () => {
    setIsDeliveryMode(prev => !prev);
  };

  const enableDeliveryMode = () => {
    setIsDeliveryMode(true);
  };

  const disableDeliveryMode = () => {
    setIsDeliveryMode(false);
  };


  const value = {
    isDeliveryMode,
    toggleDeliveryMode,
    enableDeliveryMode,
    disableDeliveryMode,
    isLoading: false,
    isRider
  };

  return (
    <DeliveryModeContext.Provider value={value}>
      {children}
    </DeliveryModeContext.Provider>
  );
};
